import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Users, Award, TrendingUp, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmtCurrency = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const colorBg = (c) => ({
  green:  'bg-emerald-500',
  amber:  'bg-amber-500',
  red:    'bg-rose-500',
  yellow: 'bg-yellow-500',
  blue:   'bg-blue-500',
}[c] || 'bg-slate-400');

const colorText = (c) => ({
  green:  'text-emerald-700',
  amber:  'text-amber-700',
  red:    'text-rose-700',
  yellow: 'text-yellow-700',
  blue:   'text-blue-700',
}[c] || 'text-slate-700');

const sparkStroke = (c) => ({
  green:  '#10b981',
  amber:  '#f59e0b',
  red:    '#f43f5e',
  yellow: '#eab308',
  blue:   '#3b82f6',
}[c] || '#64748b');

/**
 * Tiny inline SVG sparkline. Width/height fixed; renders weekly hours as a
 * polyline + tiny dots. Trend arrow indicates last-week vs prior-week direction.
 */
function WeeklySparkline({ values, color }) {
  if (!values || values.length === 0) {
    return <span className="text-[10px] text-slate-400">—</span>;
  }
  const w = 56;
  const h = 18;
  const pad = 2;
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? (w - pad * 2) / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v / max) * (h - pad * 2));
    return [x, y];
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const stroke = sparkStroke(color);

  // Trend: last vs second-last
  let trendIcon = null;
  if (values.length >= 2) {
    const last = values[values.length - 1];
    const prev = values[values.length - 2];
    if (last > prev * 1.05) trendIcon = <span className="text-emerald-600 text-[10px] ml-0.5">▲</span>;
    else if (last < prev * 0.95) trendIcon = <span className="text-rose-600 text-[10px] ml-0.5">▼</span>;
  }

  const tooltip = values.map((v, i) => `W${i + 1}: ${v.toFixed(1)}h`).join(' · ');

  return (
    <span className="inline-flex items-center" title={tooltip}>
      <svg width={w} height={h} className="overflow-visible flex-shrink-0">
        <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {points.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.5" fill={stroke} />
        ))}
      </svg>
      {trendIcon}
    </span>
  );
}

/**
 * Compact monthly mechanic productivity summary for the home dashboard.
 * Shows totals + per-mechanic progress bars for the current month.
 */
export default function MonthlyProductivitySummary({ user }) {
  const navigate = useNavigate();
  const isReadOnly = user && ['service_manager', 'floor_manager'].includes(user.role);
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/super-admin/financials/productivity-analysis?month=${month}&year=${year}`,
        { credentials: 'include' }
      );
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('productivity summary fetch failed:', e);
    } finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const monthShift = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  if (loading) {
    return (
      <Card className="p-5 bg-white border border-slate-200" data-testid="monthly-productivity-card">
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
        </div>
      </Card>
    );
  }
  if (!data) return null;

  // Sort by performance pct descending (top performers first)
  const sortedMechs = [...(data.per_mechanic || [])].sort((a, b) => b.performance_pct - a.performance_pct);
  const topPerformer = sortedMechs[0];

  return (
    <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 flex flex-col" data-testid="monthly-productivity-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-lg text-slate-900 flex items-center gap-2">
          <Users className="h-5 w-5 text-slate-700" />
          Mechanic Productivity
        </h2>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => monthShift(-1)} data-testid="prod-prev">‹</Button>
          <span className="text-sm font-medium text-slate-700 min-w-[110px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => monthShift(1)} data-testid="prod-next">›</Button>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-[10px] text-blue-600 uppercase tracking-wide">Expected</p>
          <p className="text-base font-bold text-blue-900">{(data.total_expected_hours || 0).toFixed(0)}h</p>
        </div>
        <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-md">
          <p className="text-[10px] text-emerald-600 uppercase tracking-wide">Actual</p>
          <p className="text-base font-bold text-emerald-900">{(data.total_actual_hours || 0).toFixed(0)}h</p>
        </div>
        <div className={`p-2.5 border rounded-md ${data.total_deficit_hours > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
          <p className={`text-[10px] uppercase tracking-wide ${data.total_deficit_hours > 0 ? 'text-rose-600' : 'text-slate-600'}`}>
            {data.total_deficit_hours > 0 ? 'Deficit' : 'Surplus'}
          </p>
          <p className={`text-base font-bold ${data.total_deficit_hours > 0 ? 'text-rose-900' : 'text-slate-900'}`}>
            {data.total_deficit_hours > 0 ? '−' : '+'}{Math.abs(data.total_deficit_hours || 0).toFixed(0)}h
          </p>
        </div>
      </div>

      {/* Top performer ribbon */}
      {topPerformer && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-md bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200">
          <Award className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs flex-1">
            <span className="font-semibold text-amber-900">Top performer:</span>{' '}
            <span className="text-slate-800">{topPerformer.mechanic_name}</span>{' '}
            <span className="text-amber-700">({topPerformer.performance_pct.toFixed(0)}%)</span>
          </p>
        </div>
      )}

      {/* Per-mechanic progress list */}
      <div className="flex-1 space-y-2 overflow-y-auto max-h-72 pr-1" data-testid="prod-mechanic-list">
        {sortedMechs.length === 0 && (
          <p className="text-xs text-slate-500 text-center py-4">No mechanic data for this month.</p>
        )}
        {sortedMechs.map((m) => (
          <div key={m.mechanic_id} className="flex items-center gap-2" data-testid={`prod-row-${m.mechanic_id}`}>
            <span className="text-xs font-medium text-slate-700 truncate w-32" title={m.mechanic_name}>
              {m.mechanic_name}
            </span>
            <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden relative">
              <div
                className={`absolute inset-y-0 left-0 ${colorBg(m.color)} transition-all duration-500`}
                style={{ width: `${Math.min(100, m.performance_pct)}%` }}
              />
            </div>
            <span className={`text-[11px] font-semibold w-10 text-right ${colorText(m.color)}`}>
              {m.performance_pct.toFixed(0)}%
            </span>
            <WeeklySparkline values={m.weekly_hours} color={m.color} />
            <span className="text-[11px] text-slate-500 w-16 text-right tabular-nums">
              {m.actual_hours.toFixed(0)}/{m.expected_hours.toFixed(0)}h
            </span>
          </div>
        ))}
      </div>

      {/* Footer: estimated revenue impact + link */}
      <div className="border-t mt-3 pt-3 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-slate-600">
          {isReadOnly ? (
            <>
              <TrendingUp className="h-3.5 w-3.5" />
              <span>{sortedMechs.length} mechanic{sortedMechs.length === 1 ? '' : 's'} tracked</span>
            </>
          ) : (
            <>
              <TrendingUp className="h-3.5 w-3.5" />
              <span>
                Est. revenue loss: <strong className="text-slate-900">{fmtCurrency(data.estimated_revenue_loss || 0)}</strong>
              </span>
            </>
          )}
        </div>
        <button
          onClick={() => navigate('/financials')}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
          data-testid="prod-view-full-link"
        >
          Full report <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </Card>
  );
}
